import React, { useState, useRef, useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import {
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  ChevronDownIcon,   // ⬅️ new
} from "@heroicons/react/24/outline";
import { useAuth } from "./context/AuthContext";
import { useDirectory } from "./context/DirectoryContext";
import ReactDOM from "react-dom";

function NavbarLogin() {
  // Make sure useAuth returns { user, signInWithGoogle, signOutUser }
  const { user, signInWithGoogle, signOutUser } = useAuth();

  const [showOverlay, setShowOverlay] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close user dropdown when clicking outside
  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const handleOpenLogin = () => {
    setMenuOpen(false);
    setShowOverlay(true);
  };

  const handleCloseLogin = () => {
    setShowOverlay(false);
  };

  const handleGoogleLogin = async () => {
    try {
      await signInWithGoogle();
      setShowOverlay(false);
    } catch (err) {
      console.error("Google login failed:", err);
    }
  };

  const handleLogout = async () => {
    try {
      await signOutUser();   // or logout(), depending on your AuthContext
      setMenuOpen(false);
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const firstNameRaw = user?.displayName?.split(" ")[0] || "";

  // Lowercase everything, capitalize first letter
  const firstName =
    firstNameRaw.charAt(0).toUpperCase() + firstNameRaw.slice(1).toLowerCase();

  // Trim to max 5 chars with ellipsis
  const displayName =
    firstName.length > 5 ? firstName.slice(0, 5) + "…" : firstName;

  return (
    <>
      {user ? (
        // 🔹 Logged-in state: first name + dropdown
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg
                      bg-gray-100 border border-gray-300
                      hover:bg-gray-200 active:bg-gray-300
                      transition"
          >
            <UserCircleIcon className="h-6 w-6 text-gray-700" />
            <span className="text-sm font-medium text-gray-700">
              {displayName}
            </span>
            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-32 bg-white rounded-md shadow-lg ring-1 ring-black/5 z-[9999]">
              <button
                onClick={handleLogout}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      ) : (
        // 🔹 Logged-out state: Login button
        <button
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg
                     bg-gray-100 border border-gray-300
                     hover:bg-gray-200 active:bg-gray-300
                     transition"
          onClick={handleOpenLogin}
        >
          <UserCircleIcon className="h-6 w-6 text-gray-700" />
          <span className="text-sm font-medium text-gray-700">Login</span>
        </button>
      )}

      {/* 🔹 Full-screen overlay with Google signup (via portal) */}
      {showOverlay &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm">
              <h2 className="text-lg font-semibold mb-4 text-center">
                Sign in to Scan360.in
              </h2>

              <button
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-2
                           px-4 py-2 rounded-lg border border-gray-300
                           bg-white hover:bg-gray-50 active:bg-gray-100
                           transition"
              >
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google"
                  className="h-5 w-5"
                />
                <span className="font-medium tracking-wide">
                  Login using Google
                </span>
              </button>

              <button
                onClick={handleCloseLogin}
                className="mt-4 w-full text-center text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const { directory, loading } = useDirectory();
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const [resourcesOpen, setResourcesOpen] = useState(false);
  const resourcesRef = useRef(null);

  // ---- Search state ----
  const [q, setQ] = useState("");
  const [openSug, setOpenSug] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const sugRef = useRef(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Convert {SYM: "Name"} → [{symbol, name}]
  const entries = useMemo(
    () =>
      Object.entries(directory).map(([symbol, obj]) => ({
        symbol: symbol.trim(),
        name: (obj.name || "").trim(),
        quoteType: (obj.quoteType || "").trim(),
      })),
    [directory]
  );

  // Filter suggestions by name (and symbol as fallback)
  const suggestions = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    const out = [];
    for (const e of entries) {
      const hit =
        e.name.toLowerCase().includes(s) ||
        e.symbol.toLowerCase().includes(s);
      if (hit) out.push(e);
      if (out.length >= 8) break; // cap list
    }
    return out;
  }, [q, entries]);

  const goToSymbol = (symbol) => {
    if (!symbol) return;

    // ✅ open in new tab
    window.open(`/symbol/${symbol}`, "_blank");

    // then reset your UI states
    setQ("");
    setOpenSug(false);
    setIsOpen(false);
  };

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      // nav menu
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
      // suggestions
      if (
        sugRef.current &&
        inputRef.current &&
        !sugRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setOpenSug(false);
      }

      // resources dropdown
      if (
        resourcesRef.current &&
        !resourcesRef.current.contains(event.target)
      ) {
        setResourcesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation for suggestions
  const onKeyDown = (e) => {
    if (!openSug || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const chosen = suggestions[activeIdx];
      if (chosen) {
        goToSymbol(chosen.symbol);
        setMobileSearchOpen(false);   // ✅ close mobile search if it was open
      }
    } else if (e.key === "Escape") {
      setOpenSug(false);
      setMobileSearchOpen(false);     // optional: also close on Esc
    }
  };

  const handleInputBlur = () => {
    setOpenSug(false);
  };

  useEffect(() => {
    if (mobileSearchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mobileSearchOpen]);


  const navLinks = [
    ["/", "Dashboard"],
    ["/scanners", "Scanners"],
    ["/announcements", "Announcements"],
    ["/upcoming_events", "Events"],
    ["/fund_raise", "Fund Raise"],
    ["/results", "Results"],
    ["/trades", "Trades"],
    ["/news", "News"],
    ["/resources", "Resources"],   // dropdown
    ["/playground", "Playground"],   // dropdown
    // ["/about", "About Us"],        // dropdown
    // ["/terms", "Terms"],           // dropdown
    // ["/contact", "Contact"],       // dropdown
  ];

  return (
    <nav className="sticky top-0 z-50 bg-gray-100 border-b border-gray-300 shadow-sm mb-4">
      <div className="px-4 py-2 flex items-center">
        {/* LEFT: hamburger + logo + desktop menu */}
        <div className="flex items-center space-x-4">
          {/* Hamburger (mobile) */}
          <button
            ref={buttonRef}
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden focus:outline-none"
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <XMarkIcon className="w-6 h-6 text-gray-700" />
            ) : (
              <Bars3Icon className="w-6 h-6 text-gray-700" />
            )}
          </button>

          {/* Logo */}
          <NavLink to="/" className="text-xl font-bold text-blue-700">
            Scan360.in
          </NavLink>

          {/* Desktop Menu */}
          <ul className="hidden md:flex space-x-3 text-base font-medium text-gray-700">
            {navLinks
              .filter(([to]) =>
                !["/resources", "/playground", "/about", "/terms", "/contact"].includes(to)
              )
              .map(([to, label]) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    className={({ isActive }) =>
                      `px-3 py-2 rounded transition duration-150 ease-in-out ${
                        isActive
                          ? "text-blue-600 font-semibold"
                          : "hover:text-blue-500"
                      }`
                    }
                  >
                    {label}
                  </NavLink>
                </li>
              ))}
          </ul>

          {/* Desktop Resources dropdown via arrow */}
          <div
            className="relative hidden md:block"
            ref={resourcesRef}
          >
            <button
              onClick={() => setResourcesOpen((prev) => !prev)}
              className="flex items-center px-2 py-2 rounded hover:bg-gray-200 text-gray-700"
              aria-label="Open resources menu"
            >
              <ChevronDownIcon className="w-5 h-5" />
            </button>

            {resourcesOpen && (
              <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg">
                
                <NavLink
                  to="/resources"
                  onClick={() => setResourcesOpen(false)}
                  className={({ isActive }) =>
                    `block px-4 py-2 text-sm ${
                      isActive ? "text-blue-600 font-semibold bg-blue-50" : "text-gray-700 hover:bg-gray-50"
                    }`
                  }
                >
                  Resources
                </NavLink>


                <NavLink
                  to="/playground"
                  onClick={() => setResourcesOpen(false)}
                  className={({ isActive }) =>
                    `block px-4 py-2 text-sm ${
                      isActive ? "text-blue-600 font-semibold bg-blue-50" : "text-gray-700 hover:bg-gray-50"
                    }`
                  }
                >
                  Playground
                </NavLink>

              </div>
            )}

          </div>

        </div>

        {/* RIGHT: mobile search icon + desktop search */}
        <div className="ml-auto flex items-center space-x-3">
          {/* 🔍 Mobile Search Icon */}
          <button
            className="md:hidden rounded-full hover:bg-gray-200 active:bg-gray-300"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => {
              if (mobileSearchOpen) {
                // closing
                setMobileSearchOpen(false);
                setOpenSug(false);
                if (inputRef.current) inputRef.current.blur();
              } else {
                // opening
                setMobileSearchOpen(true);
              }
            }}
          >
            {mobileSearchOpen ? (
              // ❌ CLOSE (X) ICON
              <XMarkIcon className="h-6 w-6 text-gray-700" />
            ) : (
              // 🔍 SEARCH ICON
              <MagnifyingGlassIcon className="h-6 w-6" />
            )}
          </button>

          {/* --- Search (right-aligned desktop) --- */}
          <div className="relative hidden md:block ml-4" ref={sugRef}>
            <div className="relative">
              {/* 🔍 search icon */}
              <MagnifyingGlassIcon
                className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
              />

              <input
                ref={inputRef}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setOpenSug(true);
                  setActiveIdx(0);
                }}
                onFocus={() => q && setOpenSug(true)}
                onBlur={handleInputBlur}
                onKeyDown={onKeyDown}
                placeholder="Search Stocks…"
                className="w-72 pl-8 pr-3 py-1.5 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {openSug && suggestions.length > 0 && (
              <div className="absolute right-0 mt-1 w-full max-h-80 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg">
                {suggestions.map((sug, idx) => (
                  <button
                    key={sug.symbol}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      goToSymbol(sug.symbol);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm ${
                      idx === activeIdx ? "bg-blue-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-900">{sug.name}</span>
                      <span className="ml-3 text-gray-500">{sug.quoteType}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 👤 User Login Icon (Mobile + Desktop) */}
          {/* <button
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg 
                      bg-gray-100 border border-gray-300
                      hover:bg-gray-200 active:bg-gray-300
                      transition"
          >
            <UserCircleIcon className="h-6 w-6 text-gray-700" />
            <span className="text-sm font-medium text-gray-700">Login</span>
          </button> */}
          <NavbarLogin/>
        </div>
      </div>


      {/* Mobile Menu */}
      {isOpen && (
        <div ref={menuRef} className="md:hidden bg-gray-100 border-t border-gray-300">
          {/* Mobile links */}
          <ul className="flex flex-col space-y-1 py-2 text-base font-medium text-gray-700">
            {navLinks.map(([to, label]) => (
              <li key={to}>
                <NavLink
                  to={to}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `block px-4 py-2 transition duration-150 ease-in-out ${
                      isActive
                        ? "text-blue-600 font-semibold"
                        : "hover:text-blue-500"
                    }`
                  }
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Mobile Search Box (Slide-down) */}
      {mobileSearchOpen && (
        <div className="md:hidden bg-gray-100 border-t border-gray-300 p-3" ref={sugRef}>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpenSug(true);
              setActiveIdx(0);
            }}
            onFocus={() => q && setOpenSug(true)}
            onBlur={handleInputBlur}
            onKeyDown={onKeyDown}
            placeholder="Search Stocks…"
            className={
              "w-full px-3 py-2 rounded-md bg-white text-base focus:outline-none focus:ring-2 " +
              (mobileSearchOpen
                ? "border-blue-500 focus:ring-blue-400"  // highlighted border
                : "border-gray-300 focus:ring-blue-300")
            }
          />

          {openSug && suggestions.length > 0 && (
            <div className="mt-2 max-h-80 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg">
              {suggestions.map((sug, idx) => (
                <button
                  key={sug.symbol}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    goToSymbol(sug.symbol);
                    setMobileSearchOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm ${
                    idx === activeIdx ? "bg-blue-50" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-900">{sug.name}</span>
                    <span className="ml-3 text-gray-500">{sug.quoteType}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

    </nav>
  );
}

export default Navbar;
